import {
    Box, Container, Grid, Typography,
    IconButton, Divider,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Link as RouterLink } from "react-router-dom";

/* ── Sub-components ───────────────────────────────────────── */

const FooterLink = ({ to, children }) => (
  <Typography
    component={RouterLink} to={to}
    sx={{
      display: "block",
      color: "rgba(255,255,255,0.5)",
      textDecoration: "none",
      fontSize: "0.88rem",
      mb: 2,
      letterSpacing: "0.03em",
      transition: "color 0.2s ease, padding-left 0.2s ease",
      "&:hover": { color: "secondary.main", paddingLeft: "6px" },
    }}
  >
    {children}
  </Typography>
);

const ContactRow = ({ icon: Icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2.5 }}>
    <Box sx={{
      width: 34, height: 34, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      border: "1px solid rgba(201,168,76,0.25)",
      borderRadius: "4px",
    }}>
      <Icon sx={{ fontSize: 16, color: "secondary.main" }} />
    </Box>
    <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", lineHeight: 1.7, pt: 0.5 }}>
      {children}
    </Typography>
  </Box>
);

const ColTitle = ({ children }) => (
  <Typography sx={{
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.68rem",
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    mb: 3.5,
    pb: 1.5,
    borderBottom: "1px solid rgba(201,168,76,0.2)",
  }}>
    {children}
  </Typography>
);

/* ── Footer ───────────────────────────────────────────────── */

const Footer = () => (
  <Box
    component="footer"
    sx={{
      backgroundColor: "#0d0a04",
      borderTop: "1px solid rgba(201,168,76,0.2)",
      pt: { xs: 7, md: 9 },
      pb: 4,
    }}
  >
    <Container maxWidth="xl" sx={{ px: { xs: 3, md: 6, xl: 10 } }}>
      <Grid container spacing={{ xs: 4, md: 3 }} justifyContent="space-between">

        {/* ── Col 1 : Brand ── */}
        <Grid item xs={12} md={3} sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            color: "secondary.main",
            fontSize: "1.4rem",
            lineHeight: 1.1,
            mb: 0.5,
          }}>
            LuxuryLaundry.ma
          </Typography>
          <Typography sx={{
            color: "rgba(201,168,76,0.6)",
            fontSize: "0.6rem",
            letterSpacing: "0.26em",
            textTransform: "uppercase",
            mb: 0.5,
          }}>
            Zrikem Clean SARL
          </Typography>
          <Typography sx={{
            color: "rgba(201,168,76,0.4)",
            fontSize: "0.58rem",
            letterSpacing: "0.15em",
            mb: 3,
          }}>
            RC : 114719
          </Typography>

          <Typography sx={{
            color: "rgba(255,255,255,0.45)",
            fontSize: "0.84rem",
            lineHeight: 1.85,
            mb: 3.5,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}>
            Blanchisserie de luxe à Marrakech pour hôtels, riads et particuliers.
          </Typography>

          {/* Social icons */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {[
              { Icon: InstagramIcon, href: "#", label: "Instagram" },
              { Icon: FacebookIcon, href: "#", label: "Facebook" },
              { Icon: WhatsAppIcon, href: "#", label: "WhatsApp" },
            ].map(({ Icon, href, label }) => (
              <IconButton
                key={label} component="a" href={href}
                aria-label={label}
                sx={{
                  color: "rgba(255,255,255,0.4)",
                  border: "1px solid rgba(201,168,76,0.2)",
                  borderRadius: "6px",
                  width: 40, height: 40,
                  transition: "all 0.25s ease",
                  "&:hover": {
                    color: "secondary.main",
                    borderColor: "secondary.main",
                    backgroundColor: "rgba(201,168,76,0.08)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </IconButton>
            ))}
          </Box>
        </Grid>

        {/* ── Col 2 : Navigation ── */}
        <Grid item xs={6} md={2} sx={{ minWidth: 0 }}>
          <ColTitle>Navigation</ColTitle>
          <FooterLink to="/">Accueil</FooterLink>
          <FooterLink to="/services">Services</FooterLink>
          <FooterLink to="/temoignages">Témoignages</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
        </Grid>

        {/* ── Col 3 : Services ── */}
        <Grid item xs={6} md={2} sx={{ minWidth: 0 }}>
          <ColTitle>Services</ColTitle>
          <FooterLink to="/services/blanchisserie">Blanchisserie de Luxe</FooterLink>
          <FooterLink to="/services/location-linge">Location de Linge</FooterLink>
          <FooterLink to="/services/tapis">Nettoyage de Tapis</FooterLink>
        </Grid>

        {/* ── Col 4 : Contact + Hours ── */}
        <Grid item xs={12} md={3} sx={{ minWidth: 0 }}>
          <ColTitle>Nous Contacter</ColTitle> 
          <ContactRow icon={PhoneIcon}>05 25 16 39 49 — 06 88 89 93 96</ContactRow>
          <ContactRow icon={EmailIcon}>contact@luxurylaundry.ma</ContactRow>
          <ContactRow icon={LocationOnIcon}>315, Lot Al Massar, 40000 Marrakech</ContactRow>
          <ContactRow icon={AccessTimeIcon}>
            Disponible 24h/24 — 7j/7
          </ContactRow>
        </Grid>

      </Grid>

      {/* ── Bottom bar ── */}
      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mt: 6, mb: 3 }} />
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1.5,
      }}>
        <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.78rem" }}>
          © {new Date().getFullYear()} LuxuryLaundry.ma — Tous droits réservés.
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "0.78rem" }}>
          Marrakech · Maroc
        </Typography>
      </Box>
    </Container>
  </Box>
);

export default Footer;